import React from "react";
import { MdInfo } from "react-icons/md";
import ModalShell from "./ModalShell";
import Button from "../common/Button";

interface PersistentActionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (isPersistent: boolean) => void;
  actionType: "assign" | "unassign" | "save" | "reorder";
  exerciseName?: string;
  loading?: boolean;
}

const PersistentActionConfirmationModal: React.FC<
  PersistentActionConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  exerciseName,
  loading = false,
}) => {
  if (!isOpen) return null;

  const handleThisDayOnly = () => {
    onConfirm(false);
  };

  const handleAllMatchingDays = () => {
    onConfirm(true);
  };

  const actionVerb =
    actionType === "assign"
      ? "assign"
      : actionType === "unassign"
      ? "deassign"
      : actionType === "reorder"
      ? "save the new order of"
      : "save changes for";
  const actionTitle =
    actionType === "assign"
      ? "Assign Exercise"
      : actionType === "unassign"
      ? "Deassign Exercise"
      : actionType === "reorder"
      ? "Save Exercise Order"
      : "Save Exercise Changes";

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={actionTitle}
      icon={<MdInfo className="h-6 w-6 text-biafit-primary" />}
      headerVariant="compact"
      size="md"
      layout="fixed"
      closeDisabled={loading}
      footer={
        <div className="flex w-full flex-col space-y-2">
          <Button
            fullWidth
            onClick={handleThisDayOnly}
            loading={loading}
            loadingLabel={
              actionType === "assign"
                ? "Assigning..."
                : actionType === "unassign"
                ? "Removing..."
                : "Saving..."
            }
          >
            This Day Only
          </Button>
          <Button
            variant="dark"
            fullWidth
            onClick={handleAllMatchingDays}
            loading={loading}
            loadingLabel={
              actionType === "assign"
                ? "Assigning..."
                : actionType === "unassign"
                ? "Removing..."
                : actionType === "reorder"
                ? "Updating Order..."
                : "Saving..."
            }
          >
            All Matching Days
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      }
    >
      {/* Content */}
      <div className="p-6">
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          Do you want to {actionVerb}{" "}
          <span className="font-semibold text-navy-700 dark:text-white">
            {exerciseName}
          </span>
          ?
        </p>
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {actionType === "save" || actionType === "reorder"
              ? `You can choose to save these changes to only this training day or to all training days with the same day of the week throughout the program.`
              : `You can choose to ${actionVerb} this exercise to only this training day or to all training days with the same day of the week throughout the program.`}
          </p>
        </div>
      </div>
    </ModalShell>
  );
};

export default PersistentActionConfirmationModal;
